<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import gsap from 'gsap'

const props = defineProps({
    scrollSelector: {
        type: String,
        default: '.p-datatable-wrapper'
    }
})

const container = ref(null)
const mostrarSombra = ref(false)
const cursorIndicador = ref(null)
let scrollWrapper = null
let indicadorJaExibido = false
let indicadorAtivo = false

const verificarScroll = () => {
    if (!scrollWrapper) return
    const { scrollLeft, scrollWidth, clientWidth } = scrollWrapper
    const temScrollHorizontal = scrollWidth > clientWidth
    const chegouNoFim = scrollLeft + clientWidth >= scrollWidth - 2
    mostrarSombra.value = temScrollHorizontal && !chegouNoFim
}

const onMouseEnter = () => {
    if (indicadorJaExibido) return
    if (!scrollWrapper || !cursorIndicador.value) return
    const { scrollWidth, clientWidth } = scrollWrapper
    if (scrollWidth <= clientWidth) return

    indicadorJaExibido = true
    indicadorAtivo = true
    cursorIndicador.value.style.display = 'flex'

    gsap.fromTo(cursorIndicador.value,
        { opacity: 0, scale: 0.8 },
        {
            opacity: 1, scale: 1, duration: 0.35, ease: 'back.out(1.4)',
            onComplete: () => {
                if (!cursorIndicador.value) return
                gsap.to(cursorIndicador.value, {
                    opacity: 0, scale: 0.8, duration: 0.3, ease: 'power2.in', delay: 5,
                    onComplete: () => {
                        indicadorAtivo = false
                        if (cursorIndicador.value) cursorIndicador.value.style.display = 'none'
                    }
                })
            }
        }
    )
}

const onMouseMove = (e) => {
    if (!indicadorAtivo || !cursorIndicador.value) return
    const rect = container.value.getBoundingClientRect()
    cursorIndicador.value.style.top = `${e.clientY - rect.top - 14}px`
    cursorIndicador.value.style.left = `${e.clientX - rect.left + 18}px`
}

const onMouseLeave = () => {
    if (!indicadorAtivo || !cursorIndicador.value) return
    gsap.killTweensOf(cursorIndicador.value)
    indicadorAtivo = false
    gsap.to(cursorIndicador.value, {
        opacity: 0, scale: 0.8, duration: 0.25, ease: 'power2.in',
        onComplete: () => {
            if (cursorIndicador.value) cursorIndicador.value.style.display = 'none'
        }
    })
}

const inicializar = () => {
    nextTick(() => {
        if (!container.value) return
        scrollWrapper = container.value.querySelector(props.scrollSelector)
        if (scrollWrapper) {
            scrollWrapper.addEventListener('scroll', verificarScroll)
            verificarScroll()
        }
    })
}

defineExpose({ inicializar })

onMounted(() => {
    inicializar()
    window.addEventListener('resize', verificarScroll)
})

onUnmounted(() => {
    if (scrollWrapper) {
        scrollWrapper.removeEventListener('scroll', verificarScroll)
    }
    window.removeEventListener('resize', verificarScroll)
})
</script>

<template>
    <div ref="container" class="tabela-scroll-container" :class="{ 'mostrar-sombra': mostrarSombra }"
        @mouseenter="onMouseEnter" @mousemove="onMouseMove" @mouseleave="onMouseLeave">
        <div ref="cursorIndicador" class="cursor-indicador-scroll">
            <i class="pi pi-arrow-right"></i>
        </div>
        <slot></slot>
    </div>
</template>

<style scoped>
.tabela-scroll-container {
    position: relative;
    overflow: visible;
}

.tabela-scroll-container::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 80px;
    height: 100%;
    background: linear-gradient(to right, transparent, rgb(249 250 251));
    pointer-events: none;
    z-index: 1;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.tabela-scroll-container.mostrar-sombra::after {
    opacity: 1;
}

.cursor-indicador-scroll {
    display: none;
    position: absolute;
    z-index: 10;
    pointer-events: none;
    top: 0;
    left: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--surface-0, #ffffff);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.14);
    align-items: center;
    justify-content: center;
    opacity: 0;
}

.cursor-indicador-scroll i {
    font-size: 11px;
    color: var(--text-color-secondary, #6b7280);
}
</style>
